/**
 * 학생 홈의 "오늘 목표" 카드와 학습량(낮음/중간/높음) 선택.
 * - 학습량은 난이도가 아니라 하루 분량이다. 설정은 서버(/me/study-settings)에 저장되어 재로그인·다른 기기에서도 같다.
 * - 새 학습 단위 = PDF 구역(지문) 전체, 복습 단위 = 틀린 지점 하나(풀이 화면은 전체 지문 맥락을 유지).
 * - 단위 숫자(1구역+3지점 / 1+8 / 2+15)는 내부 베타 제안 기본값이며 원장 확정값이 아니다(개발 문서·서버 ownerConfirmed=false에만 남기고 학생 화면에는 쓰지 않는다).
 * - 목표는 권장 분량일 뿐이다. 선생님이 제공한 학교 자료는 '접근 가능한 목록'이며 오늘 필수 배정이 아니다.
 *   기한 지난 복습과 배정 기한은 목표와 별개로 서버 일정대로 남는다(이 카드는 읽기만 하고 일정을 바꾸지 않는다).
 * - 세 조회(설정·복습·자료)는 각각 실패를 따로 알린다. 조회 실패를 0개(할 일 없음)로 보이지 않는다.
 * - 자동 추천은 구현하지 않았다.
 */
import { h, clear, toast } from './dom.js';
import { currentGen, type Transport } from '../core/transport.js';
import { toggleButton } from './toggle-button.js';

interface LevelOption { level: 'low' | 'mid' | 'high'; label: string; newSections: number; reviews: number }
interface Settings { level: LevelOption['level'] | null; options: LevelOption[]; goal: { newSections: number; reviews: number } | null; today: { date: string; reviewsDone: number; newSectionsStarted: number; newSectionsCompleted?: number }; note: string }
interface TaskLite { state: 'due' | 'overdue' | 'upcoming' | 'done'; kind?: string; evidenceUnavailable?: boolean }
interface MatLite { releaseId: string; assignmentId: string | null; progress?: 'new' | 'in-progress' | 'done'; selected: boolean }

export interface GoalCounts { reviewsDue: number; reviewsOverdue: number; reviewsUpcoming: number; continueN: number; newN: number }

/** 복습 목록과 자료 목록에서 홈 숫자를 센다(화면과 단위 시험이 같은 함수를 쓴다). 풀 수 없는 근거 복습은 풀 수 있는 수에 넣지 않는다. */
export function countGoal(tasks: readonly TaskLite[], mats: readonly MatLite[]): GoalCounts {
  const ok = (x: TaskLite) => !(x.kind === 'evidence-recall' && x.evidenceUnavailable === true);
  const prog = (m: MatLite) => m.progress ?? (m.selected ? 'in-progress' : 'new');
  return {
    reviewsDue: tasks.filter((x) => x.state === 'due' && ok(x)).length,
    reviewsOverdue: tasks.filter((x) => x.state === 'overdue' && ok(x)).length,
    reviewsUpcoming: tasks.filter((x) => x.state === 'upcoming').length,
    continueN: mats.filter((m) => prog(m) === 'in-progress').length,
    newN: mats.filter((m) => prog(m) === 'new').length,
  };
}

/**
 * 복습 줄 상태. 조회 성공(tasksOk)일 때만 완료로 본다. 풀 수 있는 due/overdue가 남아 있으면 완료 금지.
 * 예정 항목은 앞당기지 않으며 완료 판정에 영향을 주지 않는다. 목표(goal)와 실제 가능 수는 별개다.
 */
export function reviewLineState(tasksOk: boolean, c: GoalCounts, reviewsDone: number): 'error' | 'todo' | 'all-done' | 'none' {
  if (!tasksOk) return 'error';
  if (c.reviewsDue + c.reviewsOverdue > 0) return 'todo';
  return reviewsDone > 0 ? 'all-done' : 'none';
}

export interface GoalHandlers { onStart: (assignmentId: string) => void; goLearn: () => void; goReview: () => void }

export function mountStudyGoal(root: HTMLElement, t: Transport, hd: GoalHandlers): void {
  const gen = currentGen();
  const live = () => gen === currentGen() && root.isConnected;
  let choosing = false;
  let busy = false;

  const unit = (o: LevelOption) => `새 학습 ${o.newSections}구역 · 복습 ${o.reviews}개`;

  function picker(s: Settings): HTMLElement {
    const msg = h('p', { role: 'status', 'data-testid': 'goal-msg' });
    const group = h('div', { role: 'group', 'aria-label': '하루 학습량', class: 'toggle-group goal-levels', 'data-testid': 'goal-levels' },
      ...s.options.map((o) => h('div', { class: 'goal-level' },
        toggleButton(o.label, s.level === o.level, `goal-level-${o.level}`, () => void save(o.level, msg)),
        h('span', { class: 'ev-note', 'data-testid': `goal-unit-${o.level}` }, unit(o)))));
    return h('div', { 'data-testid': 'goal-picker' },
      h('p', {}, s.level === null ? '하루 학습량을 골라 주세요. 난이도가 아니라 하루에 할 분량이에요. 언제든 바꿀 수 있어요.' : '바꾸고 싶은 학습량을 눌러 주세요.'),
      group, h('p', { class: 'ev-note' }, s.note), msg);
  }

  async function save(level: LevelOption['level'], msg: HTMLElement): Promise<void> {
    if (busy) return; // 연속 클릭은 한 번만 보낸다
    busy = true;
    const r = await t.request('PUT', '/me/study-settings', { body: { level } });
    busy = false;
    if (!live()) return;
    if (r.kind === 'http' && r.status === 200) { choosing = false; toast('학습량을 저장했어요.'); void load(); return; }
    msg.textContent = r.kind === 'network' ? '저장 결과를 받지 못했어요. 같은 버튼을 다시 눌러 주세요.' : `저장하지 못했어요(${String(r.body['error'] ?? r.status)}).`;
  }

  async function load(): Promise<void> {
    const [sr, tr, mr] = await Promise.all([t.request('GET', '/me/study-settings'), t.request('GET', '/me/review-tasks'), t.request('GET', '/me/materials')]);
    if (!live()) return;
    clear(root);
    const box = h('section', { class: 'goal-card', 'aria-label': '오늘 목표', 'data-testid': 'study-goal' }, h('h3', {}, '오늘 목표'));
    root.append(box);
    if (sr.kind !== 'http' || sr.status !== 200) {
      box.append(h('p', { class: 'notice', role: 'alert', 'data-testid': 'goal-error' }, '학습량 설정을 불러오지 못했어요. 설정이 없는 것이 아니라 받아 오지 못한 상태예요.'),
        h('button', { class: 'secondary', 'data-testid': 'goal-retry', onclick: () => void load() }, '다시 불러오기'));
      return;
    }
    const s = sr.body as unknown as Settings;
    if (s.level === null || choosing) { box.append(picker(s)); if (s.level !== null) box.append(h('button', { class: 'secondary', onclick: () => { choosing = false; void load(); } }, '그대로 두기')); return; }
    const cur = s.options.find((o) => o.level === s.level);
    const tasksOk = tr.kind === 'http' && tr.status === 200;
    const matsOk = mr.kind === 'http' && mr.status === 200 && mr.body['state'] === 'ok';
    const mats = matsOk ? (mr.body['materials'] as MatLite[]) : [];
    const c = countGoal(tasksOk ? (tr.body['tasks'] as TaskLite[]) : [], mats);
    const goal = s.goal ?? { newSections: 0, reviews: 0 };

    box.append(h('p', { 'data-testid': 'goal-level-now' }, `학습량: ${cur?.label ?? ''} (${cur !== undefined ? unit(cur) : ''}) `,
      h('button', { class: 'secondary', 'data-testid': 'goal-change', onclick: () => { choosing = true; void load(); } }, '학습량 바꾸기')));

    // 새 학습: 시작한 수와 제출을 마친 수를 따로 보인다(시작만으로 완료로 보이지 않게). 목표는 제출 완료 기준.
    const doneN = s.today.newSectionsCompleted ?? 0;
    const newLine = h('div', { class: 'goal-row', 'data-testid': 'goal-new' }, h('strong', {}, '새 학습'),
      h('span', { 'data-testid': 'goal-new-count' }, ` 오늘 제출 완료 ${doneN} / ${goal.newSections}구역`),
      h('span', { class: 'ev-note', 'data-testid': 'goal-new-started' }, ` · 오늘 시작한 구역 ${s.today.newSectionsStarted}개`),
      doneN >= goal.newSections ? h('span', { class: 'ev-note' }, ' · 목표를 채웠어요') : null);
    if (!matsOk) newLine.append(h('p', { class: 'notice', role: 'alert', 'data-testid': 'goal-mats-error' }, '자료 목록을 불러오지 못했어요. 자료가 없는 것이 아니에요.'));
    else {
      const first = mats.find((m) => (m.progress ?? (m.selected ? 'in-progress' : 'new')) === 'in-progress' && m.assignmentId !== null);
      newLine.append(h('p', { class: 'ev-note', 'data-testid': 'goal-new-avail' }, `이어하기 ${c.continueN}개 · 시작할 학습 ${c.newN}개`));
      if (first !== undefined && first.assignmentId !== null) { const id = first.assignmentId; newLine.append(h('button', { class: 'primary', 'data-testid': 'goal-continue', onclick: () => hd.onStart(id) }, '이어서 풀기')); }
      if (c.newN > 0) newLine.append(h('button', { class: first === undefined ? 'primary' : 'secondary', 'data-testid': 'goal-start-new', onclick: hd.goLearn }, '새 구역 고르기'));
    }
    box.append(newLine);

    // 복습: 오늘 푼 수 / 목표, 지금 풀 수 있는 수, 기한 지난 수
    const revLine = h('div', { class: 'goal-row', 'data-testid': 'goal-review' }, h('strong', {}, '복습'),
      h('span', { 'data-testid': 'goal-review-count' }, ` 오늘 푼 복습 ${s.today.reviewsDone} / ${goal.reviews}개`),
      s.today.reviewsDone >= goal.reviews ? h('span', { class: 'ev-note' }, ' · 목표를 채웠어요') : null);
    if (!tasksOk) revLine.append(h('p', { class: 'notice', role: 'alert', 'data-testid': 'goal-review-error' }, '복습 할 일을 불러오지 못했어요. 할 일이 없는 것이 아니라 받아 오지 못한 상태예요.'));
    else {
      revLine.append(h('p', { class: 'ev-note', 'data-testid': 'goal-review-avail' }, `오늘 복습 ${c.reviewsDue}개 · 기한 지난 복습 ${c.reviewsOverdue}개 · 예정 ${c.reviewsUpcoming}개`));
      if (c.reviewsDue + c.reviewsOverdue > 0) revLine.append(h('button', { class: 'primary', 'data-testid': 'goal-go-review', onclick: hd.goReview }, '복습하러 가기'));
      else if (reviewLineState(tasksOk, c, s.today.reviewsDone) === 'all-done') {
        revLine.append(h('p', { class: 'ev-note', role: 'status', 'data-testid': 'goal-review-all-done' },
          `오늘 가능한 복습을 모두 마쳤어요.${s.today.reviewsDone < goal.reviews ? ` 목표 ${goal.reviews}개는 하루 권장 분량이고, 지금 풀 수 있는 복습은 더 없어요.` : ''}${c.reviewsUpcoming > 0 ? ' 예정된 복습은 일정일이 되면 열려요.' : ''}`));
      } else revLine.append(h('p', { class: 'ev-note', 'data-testid': 'goal-review-none' }, '지금 풀 복습이 없어요.'));
    }
    box.append(revLine);
    box.append(h('p', { class: 'ev-note', 'data-testid': 'goal-footnote' }, '목표는 하루 권장 분량이에요. 선생님이 제공한 학교 자료는 풀 수 있는 목록이며 오늘 꼭 해야 하는 배정이 아니에요. 기한 지난 복습과 과제 마감은 목표와 별개로 그대로 남아요.'));
  }
  void load();
}
